import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { VideoSource } from '../../../../shared/protocol';
import type { PlayerState, VideoPlayerRef } from '../../types/video';
import { getVideoCapabilities } from '../../lib/video/playerCapabilities';

type NativeSource = Extract<VideoSource, { provider: 'direct' | 'hls' }>;

interface NativeVideoPlayerProps {
  source: NativeSource;
  onReady?: () => void;
  onStateChange?: (state: PlayerState, position: number) => void;
  onSeek?: (position: number) => void;
  onError?: (message: string) => void;
}

export const NativeVideoPlayer = forwardRef<VideoPlayerRef, NativeVideoPlayerProps>(function NativeVideoPlayer({ source, onReady, onStateChange, onSeek, onError }, ref) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const remoteSeek = useRef(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const mediaUrl = source.mediaUrl;
  const provider = source.provider;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || provider !== 'hls') return;
    let cancelled = false;
    let destroyHls: (() => void) | undefined;
    const loadHls = async () => {
      try {
        const { default: Hls } = await import('hls.js');
        if (cancelled) return;
        if (Hls.isSupported()) {
          const hls = new Hls();
          destroyHls = () => hls.destroy();
          hls.on(Hls.Events.MANIFEST_PARSED, () => onReady?.());
          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (!data.fatal) return;
            const message = data.type === Hls.ErrorTypes.NETWORK_ERROR
              ? 'O HLS está indisponível ou o servidor não permite acesso CORS.'
              : 'Não foi possível reproduzir este HLS. Verifique o formato, codecs e suporte a DRM.';
            onError?.(message);
          });
          hls.loadSource(mediaUrl);
          hls.attachMedia(video);
          return;
        }
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = mediaUrl;
          return;
        }
        onError?.('Este navegador não oferece suporte à reprodução HLS.');
      } catch {
        if (!cancelled) onError?.('Não foi possível carregar o player HLS.');
      }
    };
    void loadHls();
    return () => {
      cancelled = true;
      destroyHls?.();
      video.removeAttribute('src');
      video.load();
    };
  }, [provider, mediaUrl, onError, onReady]);

  useImperativeHandle(ref, () => ({
    capabilities: getVideoCapabilities(source),
    play: async () => {
      const result = videoRef.current?.play();
      try { await result; setAutoplayBlocked(false); } catch { setAutoplayBlocked(true); }
    },
    pause: async () => { videoRef.current?.pause(); },
    seekTo: async (seconds) => {
      const video = videoRef.current;
      if (!video) return;
      remoteSeek.current = true;
      video.currentTime = Math.max(0, seconds);
      window.setTimeout(() => { remoteSeek.current = false; }, 500);
    },
    getCurrentTime: () => videoRef.current?.currentTime ?? 0,
    getDuration: () => videoRef.current?.duration || 0,
    getState: () => {
      const video = videoRef.current;
      if (!video) return 'unstarted';
      if (video.error) return 'error';
      if (video.ended) return 'ended';
      if (video.paused) return 'paused';
      if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return 'buffering';
      return 'playing';
    },
    isReady: () => Boolean(videoRef.current && videoRef.current.readyState >= HTMLMediaElement.HAVE_METADATA),
  }), [source]);

  const position = () => videoRef.current?.currentTime ?? 0;
  const showMediaError = () => {
    const code = videoRef.current?.error?.code;
    onError?.(code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
      ? 'Formato ou codec de vídeo não suportado.'
      : 'O arquivo está indisponível ou o servidor não permite acesso CORS.');
  };

  return (
    <div className="video-frame" aria-label="Player de vídeo">
      <video
        ref={videoRef}
        className="video-embed native-video"
        src={provider === 'direct' ? mediaUrl : undefined}
        controls={false}
        playsInline
        preload="metadata"
        onLoadedMetadata={() => onReady?.()}
        onCanPlay={() => onReady?.()}
        onPlay={() => { setAutoplayBlocked(false); onStateChange?.('playing', position()); }}
        onPause={() => onStateChange?.(videoRef.current?.ended ? 'ended' : 'paused', position())}
        onSeeked={() => { if (!remoteSeek.current) onSeek?.(position()); }}
        onError={showMediaError}
      />
      {autoplayBlocked && <div className="player-notice"><span>O navegador bloqueou a reprodução automática.</span><button className="text-button" onClick={() => videoRef.current?.play().then(() => setAutoplayBlocked(false)).catch(() => setAutoplayBlocked(true))}>Reproduzir</button></div>}
    </div>
  );
});
